import {
  Button,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Select,
  Spin,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { Company, CompanyType } from '../../../../types';
import { useEffect, useState } from 'react';
import { createCompany, updateCompany } from '../../../../utils/httpRequest';

export interface CreateClientModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isEdit?: boolean;
  company?: Company;
}

const CreateClientModal = ({
  open,
  onClose,
  onConfirm,
  isEdit,
  company,
}: CreateClientModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const data = {
        ...company,
        ...form.getFieldsValue(),
      };
      await (isEdit ? updateCompany(data) : createCompany(data));
      onConfirm();
    } catch (err: any) {
      if (!err?.errorFields) {
        messageApi.error(err?.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    form.resetFields();
    if (company) {
      form.setFieldsValue(company);
    }
  }, [company]);

  return (
    <Modal
      title={isEdit ? `Edit Client "${company?.name}"` : 'Add New Client'}
      open={open}
      closable
      onCancel={onClose}
      footer={
        <Flex justify="flex-end" gap={16}>
          <Button type="primary" ghost onClick={onClose} loading={loading}>
            {t('cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm} loading={loading}>
            {t('confirm')}
          </Button>
        </Flex>
      }
    >
      {contextHolder}
      <Typography.Text type="secondary">
        Please fill company information to continue.
      </Typography.Text>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" style={{ margin: 20 }}>
          <Form.Item
            label="Company name"
            name="name"
            rules={[
              {
                required: true,
                message: 'Please input company name!',
              },
            ]}
          >
            <Input type="text" placeholder="Enter" />
          </Form.Item>
          <Form.Item
            label="Type"
            name="type"
            rules={[
              {
                required: true,
                message: 'Please select company type!',
              },
            ]}
          >
            <Select
              options={[
                {
                  label: CompanyType.SOLVV,
                  value: CompanyType.SOLVV,
                },
                {
                  label: CompanyType.DEMO,
                  value: CompanyType.DEMO,
                },
                {
                  label: CompanyType.REAL,
                  value: CompanyType.REAL,
                },
              ]}
              placeholder="Select"
            />
          </Form.Item>
          <Form.Item label="Address" name="address">
            <Input type="text" placeholder="Enter" />
          </Form.Item>
          <Form.Item label="Contact" name="contact">
            <Input type="text" placeholder="Enter" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea placeholder="Enter" rows={3} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateClientModal;
